
import React, { useState } from "react";
import { Report } from "@/types/report";
import { Table, TableHeader, TableBody } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ReportsPagination } from "@/components/reports/ReportsPagination";
import { WorksheetCheckTableHeader } from "../WorksheetCheckTableHeader";
import { WorksheetCheckTableRow } from "../WorksheetCheckTableRow";
import { FilterControls } from "./FilterControls";
import { EmptyState } from "./EmptyState";
import { useWorksheetFiltering } from "@/hooks/useWorksheetFiltering";
import { mapStatusToLabel, mapStatusToColor } from "./statusMappings";

interface WorksheetCheckTableProps {
  reports: Report[];
  refetch: () => void;
}

const ITEMS_PER_PAGE = 20;

export const WorksheetCheckTable: React.FC<WorksheetCheckTableProps> = ({ reports, refetch }) => {
  const [dialogOpenFor, setDialogOpenFor] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const { toast } = useToast();
  
  const {
    paginatedReports,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    showOtherSubjectsOnly,
    setShowOtherSubjectsOnly,
    sortField,
    sortDirection,
    currentPage,
    setCurrentPage,
    totalPages,
    handleSort,
    normalizeStatus,
    resetFilters
  } = useWorksheetFiltering({
    reports,
    itemsPerPage: ITEMS_PER_PAGE
  });

  const handleApprove = async (
    reportId: string,
    status: "APPROVED" | "REJECTED",
    note: string
  ) => {
    setLoadingId(reportId);
    const userResponse = supabase.auth.getUser ? await supabase.auth.getUser() : null;
    const user = userResponse?.data?.user;
    const adminEmail = user?.email || "admin";
    const adminId = user?.id || "00000000-0000-0000-0000-000000000000"; // Provide a default UUID if not available
    
    try {
      // Find the report to get current values
      const report = reports.find(r => r.id === reportId);
      const oldStatus = report?.approved_worksheet_status || "WAITING";
      const oldNote = report?.approved_worksheet_note || "";
      
      // Only proceed with update if there's an actual change
      const statusChanged = normalizeStatus(oldStatus) !== status;
      const noteChanged = oldNote !== note;
      
      if (!statusChanged && !noteChanged) {
        toast({
          title: "ข้อมูลไม่มีการเปลี่ยนแปลง",
          description: "ไม่มีการเปลี่ยนแปลงข้อมูล",
        });
        setLoadingId(null);
        return;
      }
      
      // Update the report
      const { error } = await supabase
        .from("student_development_reports")
        .update({
          approved_worksheet_status: status,
          approved_worksheet_at: new Date().toISOString(),
          approved_worksheet_by: adminEmail,
          approved_worksheet_note: note,
        })
        .eq("id", reportId);

      if (error) {
        throw error;
      }
      
      // Log the changes
      if (statusChanged) {
        await supabase.from("report_update_logs").insert({
          report_id: reportId,
          admin_email: adminEmail,
          admin_id: adminId,
          field_changed: "approved_worksheet_status",
          old_value: oldStatus,
          new_value: status,
          reason: `แก้ไขสถานะการตรวจ worksheet จาก ${mapStatusToLabel[normalizeStatus(oldStatus)]} เป็น ${mapStatusToLabel[status]}`,
        });
      }
      
      if (noteChanged) {
        await supabase.from("report_update_logs").insert({
          report_id: reportId,
          admin_email: adminEmail,
          admin_id: adminId,
          field_changed: "approved_worksheet_note",
          old_value: oldNote,
          new_value: note,
          reason: "แก้ไขหมายเหตุการตรวจ worksheet",
        });
      }
      
      toast({
        title: "สำเร็จ",
        description: statusChanged 
          ? `${status === "APPROVED" ? "อนุมัติ" : "ไม่อนุมัติ"} worksheet เรียบร้อยแล้ว` 
          : "บันทึกการแก้ไขหมายเหตุเรียบร้อยแล้ว",
      });
      
      refetch();
    } catch (error: any) {
      toast({
        title: "เกิดข้อผิดพลาด",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <FilterControls 
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        onResetFilters={resetFilters}
        showOtherSubjectsOnly={showOtherSubjectsOnly}
        setShowOtherSubjectsOnly={setShowOtherSubjectsOnly}
      />
      
      <div className="overflow-auto">
        <Table>
          <TableHeader>
            <WorksheetCheckTableHeader 
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          </TableHeader>
          <TableBody>
            {paginatedReports.map((r) => {
              const normalizedStatus = normalizeStatus(r.approved_worksheet_status);
              return (
                <WorksheetCheckTableRow
                  key={r.id}
                  report={r}
                  normalizedStatus={normalizedStatus}
                  loadingId={loadingId}
                  dialogOpenFor={dialogOpenFor}
                  setDialogOpenFor={setDialogOpenFor}
                  handleApprove={async (status, note) => await handleApprove(r.id, status, note)}
                  mapStatusToColor={mapStatusToColor}
                  mapStatusToLabel={mapStatusToLabel}
                />
              );
            })}
          </TableBody>
        </Table>
        
        {paginatedReports.length === 0 && (
          <EmptyState hasReports={reports.length > 0} />
        )}
        
        <div className="flex justify-center mt-4">
          <ReportsPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => {
              if (page >= 1 && page <= totalPages) setCurrentPage(page);
            }}
          />
        </div>
      </div>
    </div>
  );
};
